import DocViewer from '@cyntler/react-doc-viewer';
import classNames from 'classnames';
import { useRef } from 'react';
import styles from '@/modules/admin-dashboard/components/modals/DocumentPreviewModal.module.scss';
import { safeDocViewerRenderers } from '@/modules/admin-dashboard/helpers/docViewer.ts';
import { BaseModalProps, Button, ModalContentWrapper, Spinner, TextWithShortLinks } from 'ui';
import { PdfViewer } from 'ui/PDFViewer';
import { DownloadGreyIcon } from 'ui/icons';
import { UserDocumentResponse } from 'compliance-api';
import '@cyntler/react-doc-viewer/dist/index.css';

const largePreviewFileTypes = [
  'pdf',
  'doc',
  'docx',
  'xls',
  'xlsx',
  'csv',
  'odt',
  'htm',
  'html',
  'ppt',
  'pptx',
];

interface DocumentPreviewModalProps extends BaseModalProps {
  doc: UserDocumentResponse;
  isLargePreviewAlways?: boolean;
}

const DocumentPreviewModal = ({
  open,
  doc,
  onClose,
  isLargePreviewAlways = false,
}: DocumentPreviewModalProps) => {
  const printRef = useRef<HTMLDivElement>(null);

  const isLargePreviewFileType =
    largePreviewFileTypes.includes(doc.format || '') || isLargePreviewAlways;

  const isPdf = doc.format === 'pdf';
  const isImage = ['webp', 'bmp', 'gif', 'jpg', 'jpeg', 'png', 'hiff'].includes(doc?.format || '');

  return (
    <ModalContentWrapper
      title={`${doc.name}.${doc.format}`}
      open={open}
      onClose={onClose}
      classes={{ dialogPanel: classNames({ '!max-w-[920px]': isLargePreviewFileType }) }}
    >
      <div className="flex flex-col gap-3" ref={printRef}>
        {isPdf && <PdfViewer enableZoom src={doc.url || ''} className={styles.pdfViewerWrapper} />}
        {isImage && <img alt="" src={doc.url} />}
        {!isPdf && !isImage && (
          <DocViewer
            config={{
              header: { disableHeader: true },
              noRenderer: {
                overrideComponent: () => (
                  <div className="flex h-[700px] items-center justify-center px-6">
                    <div className="flex max-w-[360px] flex-col items-center text-center">
                      <div className="text-sm font-medium text-gray-900">
                        Preview is unavailable
                      </div>
                      <div className="mt-2 text-sm leading-6 text-gray-500">
                        This file type cannot be displayed in the browser.
                      </div>
                      <a
                        href={doc.url || ''}
                        download={`${doc.name}.${doc.format}`}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-5 inline-flex"
                      >
                        <Button variant="secondary" className="w-auto gap-2 px-4">
                          <img src={DownloadGreyIcon} alt="" className="h-5 w-5" />
                          Download file
                        </Button>
                      </a>
                    </div>
                  </div>
                ),
              },
              loadingRenderer: {
                overrideComponent: () => (
                  <div className="h-[250px≈] flex items-center justify-center">
                    <Spinner />
                  </div>
                ),
              },
            }}
            pluginRenderers={safeDocViewerRenderers}
            documents={[{ fileType: doc.format, uri: doc.url || '' }]}
            className={classNames(
              '[&_[id="proxy-renderer"]]:overflow-y-auto',
              '[&_[id="image-renderer"]]:bg-none',
              { '!h-[700px] [&>div]:h-full [&>div>div]:h-full': isLargePreviewFileType },
            )}
          />
        )}

        <TextWithShortLinks text={doc.description ?? ''} />
      </div>
    </ModalContentWrapper>
  );
};

export default DocumentPreviewModal;

// const handlePrint = useCallback(() => {
//   const content = printRef.current;
//   if (!content) return;
//
//   const printWindow = window.open('', '', 'width=800,height=600');
//
//   if (!printWindow) return;
//
//   printWindow.document.write(`
//     <html>
//       <body>
//         ${content.outerHTML}
//       </body>
//     </html>
//   `);
//
//   printWindow.document.close();
//   printWindow.focus();
//
//   setTimeout(() => {
//     // printWindow.print();
//     // printWindow.close();
//   }, 3000);
// }, []);

// const title = (
//   <div className="flex gap-2">
//     <div>{doc.name}</div>
//     <button onClick={handlePrint}>print</button>
//   </div>
// );
